import {
    clientFieldError,
    duplicateIdErrors,
    isBlankValue,
    isDestinationField,
    type ProviderFieldError,
    type ProviderFieldSchema,
    type ProviderGroup,
    type ProviderSchema,
    SECRET_COLUMN,
    SECRET_NAMES,
    type SecretName,
    scadaItemCount,
    secretNamesOf,
} from '@/domain/providerConfig';
import { sameValue } from '@/routes/shared/ProviderConfig/form/formLeaves';
import { toFormPath } from '@/routes/shared/ProviderConfig/form/serverErrors';

import { destinationFields, type EffectivePen, effectiveSave, storedValue } from './effectiveRows';
import type { FormDiff } from './formDiff';
import { heldKeys } from './heldValues';

/**
 * The server's save rules over the pens the save would leave (provider-config-validation.ts,
 * duplicate-ids.ts, destination-secrets.ts), for feedback before the request. The server
 * stays the authority: where the client cannot see a value (an advanced one for a client
 * admin) the rule passes rather than guesses.
 */
const SIGNAL_PREFIX = 'signal_mappings.';
const SCADA_ITEMS_KEY = 'extra_config.scada_items';

class ErrorList {
    private readonly seen = new Set<string>();
    readonly errors: ProviderFieldError[] = [];

    add(serverPath: string, code: string): void {
        const path = toFormPath(serverPath);
        const key = `${path}\u0000${code}`;
        if (this.seen.has(key)) return;
        this.seen.add(key);
        this.errors.push({ path, code });
    }
}

function secretNameOf(column: string): SecretName | null {
    return SECRET_NAMES.find((name) => SECRET_COLUMN[name] === column) ?? null;
}

type Held = ReadonlySet<string>;

function isSet(pen: EffectivePen, held: Held, key: string): boolean {
    return held.has(key) || !isBlankValue(pen.values[key] ?? null);
}

function mapsDailySignals(pen: EffectivePen, held: Held): boolean {
    const keys = new Set([...Object.keys(pen.values), ...held]);
    return [...keys].some((key) => key.startsWith(SIGNAL_PREFIX) && isSet(pen, held, key));
}

/** S1: a Clarify pen with SCADA items and no daily mapping is not bound by the daily rules. */
function isScadaOnly(schema: ProviderSchema, pen: EffectivePen, held: Held): boolean {
    if (schema.providerName !== 'clarify' || mapsDailySignals(pen, held)) return false;
    return held.has(SCADA_ITEMS_KEY) || scadaItemCount(pen.values[SCADA_ITEMS_KEY] ?? null) > 0;
}

function needsPopulationSignal(schema: ProviderSchema, pen: EffectivePen, held: Held): boolean {
    if (schema.providerName !== 'clarify' || !pen.isActive || !mapsDailySignals(pen, held))
        return false;
    return ['fish_count', 'biomass'].every(
        (signal) => !isSet(pen, held, `${SIGNAL_PREFIX}${signal}`)
    );
}

/** Whether any pen of the site holds the secret, which a new pen could then have reused. */
function siteHolds(group: ProviderGroup | null, name: SecretName): boolean {
    return (group?.credentials[name]?.state ?? 'notSet') !== 'notSet';
}

function checkSecret(
    group: ProviderGroup | null,
    diff: FormDiff,
    pen: EffectivePen,
    field: ProviderFieldSchema,
    errors: ErrorList
): void {
    const name = secretNameOf(field.key);
    if (name === null || pen.secretsSet[name]) return;
    if (diff.secrets[name] !== undefined) errors.add(`secrets.${name}`, 'required');
    else if (pen.row !== null) errors.add(`pens.${pen.cageId}.secrets.${name}`, 'required');
    // With nothing stored at the site the site credential is what is missing; only where the
    // site holds one that this pen cannot reuse does the pen need its own.
    else if (!siteHolds(group, name)) errors.add(`secrets.${name}`, 'required');
    else errors.add(`pens.${pen.cageId}.secrets.${name}`, 'newPenSecretRequired');
}

/**
 * Where a pen's problem with a field is shown. A site field has one input for the site
 * unless the pens hold different values, so the pen's own path would be out of sight.
 */
function pathFor(
    group: ProviderGroup | null,
    diff: FormDiff,
    cageId: number,
    field: ProviderFieldSchema
): string {
    const ownInput = field.scope === 'pen' || group?.connection[field.key]?.varies === true;
    return field.key in diff.connection || !ownInput
        ? `connection.${field.key}`
        : `pens.${cageId}.${field.key}`;
}

function checkPen(
    schema: ProviderSchema,
    group: ProviderGroup | null,
    diff: FormDiff,
    pen: EffectivePen,
    errors: ErrorList
): void {
    // A value withheld from the viewer is still stored, so it counts as set.
    const held = heldKeys(diff, pen);
    const scadaOnly = isScadaOnly(schema, pen, held);
    // A key no save can write (the server's `blocked`) is not required, as on the server.
    const blocked = (field: ProviderFieldSchema): boolean =>
        pen.row?.unreadable.some((entry) => entry.key === field.key && entry.blocked) ?? false;
    const requires = (field: ProviderFieldSchema): boolean =>
        pen.isActive &&
        field.required &&
        !blocked(field) &&
        !(scadaOnly && field.key.startsWith(SIGNAL_PREFIX));
    for (const field of schema.fields) {
        if (field.kind === 'secret') {
            if (requires(field)) checkSecret(group, diff, pen, field, errors);
            continue;
        }
        const value = pen.values[field.key] ?? null;
        // As on the server, only a value the save writes is format-checked (R6-1).
        const written = field.key in (pen.diff?.fields ?? {}) || field.key in diff.connection;
        const code = written || isDestinationField(field) ? clientFieldError(field, value) : null;
        const path = pathFor(group, diff, pen.cageId, field);
        if (code !== null) errors.add(path, code);
        else if (!isSet(pen, held, field.key) && requires(field)) errors.add(path, 'required');
    }
    if (needsPopulationSignal(schema, pen, held))
        errors.add(`pens.${pen.cageId}.${SIGNAL_PREFIX}fish_count`, 'clarifyPopulationSignal');
}

/**
 * R20: a stored credential never follows a changed destination for a client admin, who
 * retypes (or clears) it. An OptoScale admin may reuse it, which the server audits.
 */
function checkMovedCredentials(
    schema: ProviderSchema,
    group: ProviderGroup,
    diff: FormDiff,
    pen: EffectivePen,
    errors: ErrorList
): void {
    if (pen.row === null) return;
    const row = pen.row;
    const own = pen.diff?.fields ?? {};
    const moved = destinationFields(schema)
        .filter(
            (field) => !sameValue(storedValue(group, row, field), pen.values[field.key] ?? null)
        )
        .map((field) => field.key);
    if (moved.length === 0) return;
    const fromSite = moved.every((key) => key in diff.connection && !(key in own));
    for (const name of secretNamesOf(schema)) {
        const named = pen.diff?.secrets[name] !== undefined || diff.secrets[name] !== undefined;
        if (named || !row.credentials[name]) continue;
        errors.add(
            fromSite ? `secrets.${name}` : `pens.${pen.cageId}.secrets.${name}`,
            'secretRequiredForNewDestination'
        );
    }
}

/** The client's errors for a save of `diff`, at form paths. */
export function clientFormErrors(
    schema: ProviderSchema,
    group: ProviderGroup | null,
    diff: FormDiff,
    options: { isOptoScaleAdmin: boolean }
): ProviderFieldError[] {
    const errors = new ErrorList();
    const { pens, touched } = effectiveSave(schema, group, diff);
    for (const pen of pens) {
        if (!touched.has(pen.cageId)) continue;
        checkPen(schema, group, diff, pen, errors);
        if (group !== null && !options.isOptoScaleAdmin)
            checkMovedCredentials(schema, group, diff, pen, errors);
    }
    for (const error of duplicateIdErrors(schema, pens, touched))
        errors.add(error.path, error.code);
    return errors.errors;
}
